import dayjs from 'dayjs';
import { routes } from '../../utils/constants/adminRoutes';
import { NotificationsDetails } from './NotificationsDetails';

/**
 * Handles the rendering of notification menu item links based on the provided parameters.
 * @function
 *
 * @param {string} notifID - The ID of the notification.
 * @param {function} deleteNotification - The function to delete the notification.
 * @param {object} t - The translation object.
 * @param {string} createdAt - The creation date of the notification.
 * @param {boolean} consulted - Indicates whether the notification has been consulted.
 * @param {object} item - The notification item object.
 * @param {string} variant - Display variant of the notification ('list' or 'card').
 * @param {Function} onOpen - Called after the notification has been opened (row variant).
 * @returns {JSX.Element} - The rendered notification link component.
 */
export const handleNotifMenuItemLink = (
  notifID,
  deleteNotification,
  t,
  createdAt,
  consulted,
  item = {},
  variant = 'list',
  onOpen = undefined
) => {
  const { type } = item || {};

  const { _id, reference, supervision, main_informations } = type || {};

  const { start_date } = main_informations || {};

  const action = item?.action;
  const ref = item?.ref;

  const id = _id ?? '';

  const getActionLabelKey = () => {
    switch (ref) {
      case 'Supervision':
        return action === 'create' ? 'process_request' : 'view_supervision';
      case 'Rating':
        return 'view_review';
      case 'Reservation':
        return 'view_reservation';
      case 'Customerinvoice':
        return 'view_invoice';
      default:
        return 'view_profile';
    }
  };
  const actionLabel = t(`notifications.actions.${getActionLabelKey()}`);

  const displayNotificationLink = () => {
    switch (true) {
      case ref === 'Supervision' && action === 'create':
        return (
          <NotificationsDetails
            actionLabel={actionLabel}
            variant={variant}
            kind={`${ref}:${action}`}
            onOpen={onOpen}
            content={t('notifications.new_supervision')}
            pathname={`${routes.SUPERVISIONS}/show/${id}`}
            notifID={notifID}
            createdAt={createdAt}
            deleteNotification={deleteNotification}
            consulted={consulted}
          />
        );
      case ref === 'Supervision' && action === 'owner_accept_pensioner':
        return (
          <NotificationsDetails
            actionLabel={actionLabel}
            variant={variant}
            kind={`${ref}:${action}`}
            onOpen={onOpen}
            content={t('notifications.accept_pensioners', { reference })}
            pathname={`${routes.SUPERVISIONS}/show/${id}`}
            notifID={notifID}
            createdAt={createdAt}
            deleteNotification={deleteNotification}
            consulted={consulted}
          />
        );
      case ref === 'Supervision' && action === 'tomorrow_supervision':
        return (
          <NotificationsDetails
            actionLabel={actionLabel}
            variant={variant}
            kind={`${ref}:${action}`}
            onOpen={onOpen}
            content={t('notifications.tomorrow_supervision', {
              reference,
              start_date: dayjs(start_date).format('DD/MM/YYYY')
            })}
            pathname={`${routes.SUPERVISIONS}/show/${id}`}
            notifID={notifID}
            createdAt={createdAt}
            deleteNotification={deleteNotification}
            consulted={consulted}
          />
        );
      case ref === 'Supervision' && action === 'supervision_finished':
        return (
          <NotificationsDetails
            actionLabel={actionLabel}
            variant={variant}
            kind={`${ref}:${action}`}
            onOpen={onOpen}
            content={t('notifications.supervision_finished', {
              reference
            })}
            pathname={`${routes.SUPERVISIONS}/show/${id}`}
            notifID={notifID}
            createdAt={createdAt}
            deleteNotification={deleteNotification}
            consulted={consulted}
          />
        );
      case ref === 'Rating' && action === 'create':
        return (
          <NotificationsDetails
            actionLabel={actionLabel}
            variant={variant}
            kind={`${ref}:${action}`}
            onOpen={onOpen}
            content={t('notifications.new_review')}
            pathname={`${routes.SUPERVISIONS}/show/${supervision}`}
            notifID={notifID}
            createdAt={createdAt}
            deleteNotification={deleteNotification}
            consulted={consulted}
          />
        );
      case ref === 'Owner' && action === 'create':
        return (
          <NotificationsDetails
            actionLabel={actionLabel}
            variant={variant}
            kind={`${ref}:${action}`}
            onOpen={onOpen}
            content={t('notifications.new_owner')}
            pathname={`${routes.OWNERS}/show/${id}`}
            notifID={notifID}
            createdAt={createdAt}
            deleteNotification={deleteNotification}
            consulted={consulted}
          />
        );
      case ref === 'Pensioner' && action === 'create':
        return (
          <NotificationsDetails
            actionLabel={actionLabel}
            variant={variant}
            kind={`${ref}:${action}`}
            onOpen={onOpen}
            content={t('notifications.new_pensioner')}
            pathname={`${routes.PENSIONERS}/show/${id}`}
            notifID={notifID}
            createdAt={createdAt}
            deleteNotification={deleteNotification}
            consulted={consulted}
          />
        );
      case ref === 'Reservation' && action === 'create':
        return (
          <NotificationsDetails
            actionLabel={actionLabel}
            variant={variant}
            kind={`${ref}:${action}`}
            onOpen={onOpen}
            content={t('notifications.new_reservation')}
            pathname={`${routes.SUPERVISIONS}/show/${supervision}`}
            notifID={notifID}
            createdAt={createdAt}
            deleteNotification={deleteNotification}
            consulted={consulted}
          />
        );
      case ref === 'Customerinvoice' && action === 'unpaid_owners_invoices':
        return (
          <NotificationsDetails
            actionLabel={actionLabel}
            variant={variant}
            kind={`${ref}:${action}`}
            onOpen={onOpen}
            content={t('notifications.unpaid_owners_invoices', { reference })}
            pathname={`${routes.INVOICES}/customers/show/${id}`}
            notifID={notifID}
            createdAt={createdAt}
            deleteNotification={deleteNotification}
            consulted={consulted}
          />
        );
      default:
        return t('notifications.form.unknown-type');
    }
  };

  return displayNotificationLink();
};
